import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, StatusBadge, MetricCard, EmptyState } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, Wallet } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";

export default function Withdrawal() {
  const { data: dash, reload: reloadDash } = useFetch("/user/dashboard");
  const { data, reload } = useFetch("/user/withdrawals");
  const [f, setF] = useState({ amount: "", coin: "USDT", network: "BEP20", wallet_address: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/user/withdrawals", { ...f, amount: Number(f.amount) });
      toast.success("Withdrawal requested"); setF({ ...f, amount: "", wallet_address: "" }); reload(); reloadDash();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const avail = dash?.balances?.available || 0;
  return (
    <div className="space-y-6">
      <PageTitle title="Withdrawal" subtitle="Request a payout to your crypto wallet" />
      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="Available Balance" value={`$${money(avail)}`} icon={Wallet} accent />
        <MetricCard label="On Hold" value={`$${money(dash?.balances?.on_hold || 0)}`} icon={Wallet} />
        <MetricCard label="Paid Out" value={`$${money(dash?.balances?.paid || 0)}`} icon={ArrowDownToLine} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <h3 className="mb-4 font-semibold text-white">New Withdrawal</h3>
          <form onSubmit={submit} className="space-y-3">
            <input className={inp} type="number" step="any" placeholder="Amount (USDT)" value={f.amount} onChange={upd("amount")} data-testid="wd-amount" required />
            {f.amount && Number(f.amount) > 0 && (
              <div className="space-y-1 rounded-xl glass-2 p-3 text-xs" data-testid="wd-fee-preview">
                {Math.floor(Number(f.amount) / 10) * 10 !== Number(f.amount) && Math.floor(Number(f.amount) / 10) * 10 >= 10 && (
                  <div className="rounded-lg bg-[var(--maroon)]/10 p-2 text-[var(--maroon)]" data-testid="wd-multiple-note">
                    Withdrawals are processed in multiples of $10. ${money(Math.floor(Number(f.amount) / 10) * 10)} will be withdrawn; ${money(Number(f.amount) - Math.floor(Number(f.amount) / 10) * 10)} stays in your wallet.
                  </div>
                )}
                {(() => { const amt = Math.max(Math.floor(Number(f.amount) / 10) * 10, 0); return (<>
                <div className="flex justify-between"><span className="text-[var(--text-dim)]">Withdrawal amount</span><span className="font-medium">${money(amt)}</span></div>
                <div className="flex justify-between text-[var(--maroon)]"><span>Charge (10% admin + service)</span><span>- ${money(amt * 0.1)}</span></div>
                <div className="flex justify-between border-t border-[var(--line)] pt-1 text-sm font-bold text-[var(--royal)]"><span>You'll receive</span><span data-testid="wd-net-preview">${money(amt * 0.9)}</span></div>
                </>); })()}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input className={inp} placeholder="Coin" value={f.coin} onChange={upd("coin")} data-testid="wd-coin" />
              <input className={inp} placeholder="Network" value={f.network} onChange={upd("network")} data-testid="wd-network" />
            </div>
            <input className={inp} placeholder="Destination wallet address" value={f.wallet_address} onChange={upd("wallet_address")} data-testid="wd-address" required />
            <button type="submit" disabled={busy} className="btn3d btn3d-yellow w-full rounded-xl py-3 disabled:opacity-50" data-testid="wd-submit">Request Withdrawal</button>
          </form>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="mb-4 font-semibold text-white">How your payout works</h3>
          <div className="space-y-1.5 rounded-xl glass-2 p-4 text-xs text-[var(--text-dim)]" data-testid="wd-info">
            <p>• <b>Network:</b> USDT BEP20 (Binance Smart Chain). Only use a BEP20-compatible wallet address.</p>
            <p>• <b>Destination:</b> funds are sent to the exact wallet address you enter above — please double-check it, on-chain transfers are irreversible.</p>
            <p>• <b>Minimum:</b> $10 USDT. Only your available <b>profit</b> is withdrawable; your activation principal stays locked.</p>
            <p>• <b>Charges:</b> a flat <b>10%</b> deduction (admin + service charge) applies to every withdrawal; the <b>net</b> amount is shown after review.</p>
          </div>
          <p className="mt-3 text-xs text-[var(--text-dim)]">View all your past requests under <b className="text-[var(--royal)]">Withdrawal → Withdrawal History</b>.</p>
        </GlassCard>
      </div>
    </div>
  );
}
